// eslint-disable-next-line import/no-unresolved
import { Content } from '@plone/types';
import { BaseSchema, Schema } from './types';

interface SlateNode {
  text?: string;
  children?: SlateNode[];
}

interface BlockContainer {
  blocks?: Record<string, FaqBlock>;
  blocks_layout?: { items?: string[] };
}

interface FaqBlock extends BlockContainer {
  ['@type']?: string;
  faq?: boolean;
  title?: string;
  plaintext?: string;
  value?: unknown[];
  data?: BlockContainer;
}

const normalize = (text: string): string => text.replace(/\s+/g, ' ').trim();

const orderedBlocks = (container?: BlockContainer): FaqBlock[] => {
  const blocks = container?.blocks || {};
  const items = container?.blocks_layout?.items || Object.keys(blocks);

  return items.map((id) => blocks[id]).filter(Boolean);
};

const nodeText = (node: SlateNode): string =>
  typeof node?.text === 'string'
    ? node.text
    : Array.isArray(node?.children)
      ? node.children.map(nodeText).join(' ')
      : '';

const blockText = (block: FaqBlock): string =>
  block['@type'] !== 'slate'
    ? ''
    : block.plaintext?.trim()
      ? normalize(block.plaintext)
      : normalize(
          Array.isArray(block.value)
            ? (block.value as SlateNode[]).map(nodeText).join(' ')
            : '',
        );

const answerText = (panel: FaqBlock): string =>
  orderedBlocks(panel)
    .map(blockText)
    .filter((text) => text.length > 0)
    .join(' ')
    .trim();

const toQuestion = (panel: FaqBlock): BaseSchema | null => {
  const name = normalize(panel.title || '');
  const text = answerText(panel);

  return name && text
    ? {
        '@type': 'Question',
        name,
        acceptedAnswer: {
          '@type': 'Answer',
          text,
        },
      }
    : null;
};

const collectFaqAccordions = (container?: BlockContainer): FaqBlock[] =>
  orderedBlocks(container).flatMap((block) => [
    ...(block['@type'] === 'accordion' && block.faq === true ? [block] : []),
    ...collectFaqAccordions(block.data),
    ...collectFaqAccordions(block),
  ]);

/**
 * Collects every accordion marked as a FAQ into a single FAQPage node, so a
 * page never carries more than one of them. #1646
 */
export const generateFaqPage = (content: Content): Schema | null => {
  const questions = collectFaqAccordions(content as BlockContainer)
    .flatMap((accordion) => orderedBlocks(accordion.data))
    .map(toQuestion)
    .filter((question): question is BaseSchema => question !== null);

  return questions.length
    ? {
        '@type': 'FAQPage',
        mainEntity: questions,
      }
    : null;
};
